import { useState } from "react";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { BrutalSelect, BrutalSelectTrigger, BrutalSelectValue, BrutalSelectContent, BrutalSelectItem } from "@/components/ui/BrutalSelect";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { supabase } from "@/integrations/supabase/client";
import { Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { sanitizeName, sanitizeText, sanitizeUrl } from "@/lib/sanitize";

interface SocialSubmissionFormProps {
  onBack: () => void;
  onSubmit: (data: {
    platform: string;
    title: string;
    url: string;
    screenshot: File | null;
    notes: string;
  }) => Promise<void>;
  isSubmitting: boolean;
}

export const SocialSubmissionForm = ({ onBack, onSubmit, isSubmitting }: SocialSubmissionFormProps) => {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState("");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const platforms = [
    { value: "instagram", label: "Instagram 📱" },
    { value: "linkedin", label: "LinkedIn 💼" },
    { value: "facebook", label: "Facebook 👥" },
    { value: "tiktok", label: "TikTok 🎵" },
    { value: "youtube", label: "YouTube ▶️" },
  ];

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!platform) {
      newErrors.platform = t("creator.validation.platformRequired");
    }

    if (title.length < 10) {
      newErrors.title = "Titolo troppo corto (min 10 caratteri)";
    }

    if (title.length > 100) {
      newErrors.title = "Titolo troppo lungo (max 100 caratteri)";
    }

    try {
      new URL(url);
    } catch {
      newErrors.url = t("creator.validation.urlInvalid");
    }

    if (!screenshot) {
      newErrors.screenshot = t("creator.validation.screenshotRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    await onSubmit({
      platform,
      title: sanitizeName(title),
      url: sanitizeUrl(url),
      screenshot,
      notes: sanitizeText(notes),
    });
  };

  return (
    <BrutalCard className="mb-8">
      <div className="flex items-center gap-4 mb-6">
        <BrutalButton
          type="button"
          variant="outline"
          size="sm"
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("creator.backToSelection")}
        </BrutalButton>
        <h2 className="font-heading text-2xl">{t("creator.socialForm.title")}</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block font-heading text-sm mb-2">
            {t("creator.socialForm.platform")} *
          </label>
          <BrutalSelect value={platform} onValueChange={setPlatform}>
            <BrutalSelectTrigger>
              <BrutalSelectValue placeholder={t("creator.socialForm.selectPlatform")} />
            </BrutalSelectTrigger>
            <BrutalSelectContent>
              {platforms.map((p) => (
                <BrutalSelectItem key={p.value} value={p.value}>
                  {p.label}
                </BrutalSelectItem>
              ))}
            </BrutalSelectContent>
          </BrutalSelect>
          {errors.platform && (
            <p className="text-warning text-sm mt-1">{errors.platform}</p>
          )}
        </div>

        <div>
          <label className="block font-heading text-sm mb-2">
            {t("creator.socialForm.postTitle")} *
          </label>
          <BrutalInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Es: Post su inclusività e diversità"
            maxLength={100}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {title.length}/100 caratteri
          </p>
          {errors.title && (
            <p className="text-warning text-sm mt-1">{errors.title}</p>
          )}
        </div>

        <div>
          <label className="block font-heading text-sm mb-2">
            {t("creator.socialForm.postUrl")} *
          </label>
          <BrutalInput
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
          />
          {errors.url && (
            <p className="text-warning text-sm mt-1">{errors.url}</p>
          )}
        </div>

        <div>
          <label className="block font-heading text-sm mb-2">
            {t("creator.socialForm.screenshot")} *
          </label>
          <div className="border-[3px] border-dark p-4">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) setScreenshot(file);
              }}
              className="hidden"
              id="screenshot-upload"
            />
            <label
              htmlFor="screenshot-upload"
              className="flex flex-col items-center gap-2 p-6 border-[3px] border-dashed border-dark cursor-pointer hover:bg-accent/10 transition-colors"
            >
              {screenshot ? (
                <div className="relative">
                  <img
                    src={URL.createObjectURL(screenshot)}
                    alt="Screenshot preview"
                    className="max-h-48 border-2 border-dark"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setScreenshot(null);
                    }}
                    className="absolute -top-2 -right-2 bg-destructive text-white p-1 rounded-full border-2 border-dark"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <p className="text-sm font-medium">Carica screenshot</p>
                  <p className="text-xs text-muted-foreground">JPG, PNG o WEBP • Max 5MB</p>
                </>
              )}
            </label>
          </div>
          {errors.screenshot && (
            <p className="text-warning text-sm mt-1">{errors.screenshot}</p>
          )}
        </div>

        <div>
          <label className="block font-heading text-sm mb-2">
            {t("creator.socialForm.notes")}
          </label>
          <BrutalTextarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Note aggiuntive..."
            rows={3}
          />
        </div>

        <div className="border-t-[3px] border-dark pt-6">
          <BrutalButton
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? "Invio in corso..." : t("creator.socialForm.submit")}
          </BrutalButton>
        </div>
      </form>
    </BrutalCard>
  );
};
